import { useEffect } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { motion } from 'framer-motion'
import { ChevronRightIcon, PencilIcon } from '@heroicons/react/24/outline'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../contexts/AuthContext'
import FormPageSkeleton from '../components/common/FormPageSkeleton'
import ErrorMessage from '../components/common/ErrorMessage'
import SaleForm from '../components/sales/SaleForm'
import { api } from '../services/api'
import { idFromSlug, eventPath, salePath } from '../utils/slugs'

export default function EditSalePage() {
  usePageTitle('Edit Garage Sale')
  const navigate = useNavigate()
  const { eventId: eventSlug, id: saleSlug } = useParams()
  const eventId = idFromSlug(eventSlug)
  const id = idFromSlug(saleSlug)
  const { user } = useAuth()
  const { execute: submitSale, loading: submitting, error: submitError } = useApi()
  const { data: sale, loading: saleLoading, error: saleError, execute: fetchSale } = useApi()
  const { data: event, loading: eventLoading, error: eventError, execute: fetchEvent } = useApi()

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: `/events/${eventSlug}/sales/${saleSlug}/edit` } })
    }
  }, [user, navigate, eventSlug, saleSlug])

  useEffect(() => {
    if (!eventId || !id) {
      navigate('/events')
      return
    }
    fetchSale(() => api.sales.edit(eventId, id))
    fetchEvent(() => api.events.get(eventId))
  }, [eventId, id, navigate])

  const handleSubmit = async (formData) => {
    try {
      await submitSale(() => api.sales.update(eventId, id, formData))
      navigate(sale && event ? salePath(event, sale) : '/events')
    } catch (error) {
      console.error('Error updating sale:', error)
    }
  }

  const handleCancel = () => {
    navigate(sale && event ? salePath(event, sale) : '/events')
  }

  if (saleLoading || eventLoading) {
    return <FormPageSkeleton />
  }

  if (saleError || eventError) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <ErrorMessage
          message={saleError?.message || eventError?.message}
          onRetry={() => {
            if (saleError) fetchSale(() => api.sales.edit(eventId, id))
            if (eventError) fetchEvent(() => api.events.get(eventId))
          }}
        />
      </div>
    )
  }

  if (!sale || !event) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="empty-state">
          <p className="empty-state-title">Sale not found</p>
          <p className="empty-state-description">This sale may have been removed or the link is incorrect.</p>
          <Link to="/events" className="btn-primary mt-4">Browse Events</Link>
        </div>
      </div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
    >
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-text-muted mb-6 overflow-x-auto">
        <Link to="/events" className="link-subtle hover:text-text whitespace-nowrap">Events</Link>
        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
        <Link to={eventPath(event)} className="link-subtle hover:text-text truncate max-w-[120px] sm:max-w-[150px]">
          {event.name}
        </Link>
        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
        <Link to={salePath(event, sale)} className="link-subtle hover:text-text truncate max-w-[120px] sm:max-w-[150px]">
          {sale.title}
        </Link>
        <ChevronRightIcon className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="text-text font-medium whitespace-nowrap">Edit</span>
      </nav>

      {/* Page Header */}
      <div className="flex items-start gap-3.5 mb-8">
        <div className="rounded-xl bg-accent-50 dark:bg-accent-50 p-2.5 flex-shrink-0
          ring-4 ring-accent-50/50 dark:ring-accent-50/30">
          <PencilIcon className="h-6 w-6 text-accent-600 dark:text-accent-500" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text leading-tight">Edit Your Sale</h1>
          <p className="text-text-muted mt-1 text-sm sm:text-base">
            Update the details of "{sale.title}"
          </p>
        </div>
      </div>

      <SaleForm
        mode="edit"
        initialData={sale}
        event={event}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        loading={submitting}
        error={submitError}
        submitButtonText="Update Sale"
        loadingText="Updating..."
      />
    </motion.div>
  )
}
